/**
 * Shared JSON Schema parsing helpers — consumed by both ora (JsonSchemaForm.tsx)
 * and the Outlook add-in (SchemaForm.tsx).
 *
 * Import alias in ora:     ~/common/workflows/schemaHelpers
 * Import alias in add-in:  @product-app/common/workflows/schemaHelpers
 *
 * The FieldSchema interface is the canonical superset (add-in version) so the
 * add-in's extended field types (multiline, array_of_objects) are represented.
 * Ora's renderer falls back to the JSON textarea for those types gracefully.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FieldSchema {
  key: string;
  label: string;
  description?: string;
  type:
    | "string"
    | "number"
    | "integer"
    | "boolean"
    | "enum"
    | "unknown"
    | "multiline"
    | "array_of_objects";
  /** JSON Schema ``format`` hint — e.g. ``"password"`` hides the value in the UI. */
  format?: string;
  /** ``x-sensitive`` annotation — ``"password"`` | ``"secret"`` | ``"pii"``. */
  xSensitive?: string;
  enumValues?: string[];
  /**
   * Display labels for enum options — parallel array to ``enumValues``, sourced
   * from the schema's ``x-enum-titles``. Lets the substrate keep stable enum
   * keys (e.g. ``"text_sms"``) while surfacing AM-friendly labels (e.g.
   * ``"Text a code to my phone"``) in pill and dropdown rendering. Falls back
   * to ``titleCaseKey(enumValues[i])`` per option when the array is absent or
   * the index is out of range.
   */
  enumLabels?: string[];
  /**
   * Render-hint annotation from the schema's ``x-render`` extension. Lets the
   * substrate override the default control type per field — typically used to
   * force a *selection* control (radio buttons) instead of the pill-button
   * mode JsonSchemaForm uses for enums when ``submitOnPillSelect`` is true.
   * Pills are commit affordances; mixing them with a separate decision pill
   * row in the same form confuses AMs.
   */
  xRender?: string;
  /**
   * Layout annotation from the schema's ``x-render-direction`` extension.
   * Only consulted by the radio-group rendering (``x-render: "radio"``):
   * ``"horizontal"`` lays the options out in a wrapping row — for short
   * verb-like choices (Add / Change / Delete) where a vertical stack reads
   * as three separate questions. Anything else keeps the vertical default.
   */
  xRenderDirection?: string;
  /**
   * Widget-selector annotation from the schema's ``x-widget`` extension.
   * Lets the substrate request a completely different widget than the
   * default for the field's ``type``. Supported values:
   *
   *   - ``"file_upload"`` — render a drag-and-drop file picker; emit
   *     ``[{name, mime_type, bytes_b64}, ...]`` as the field value.
   *     Used by workflows whose launch form needs file inputs (e.g.
   *     test_draft_reply_email_setup's ``launch_attachments``).
   *
   * Document new ``x-widget`` values in
   * ``docs/superpowers/workflow-conventions.md`` when adding them so the
   * convention doesn't atrophy or get reinvented as ``x-render`` /
   * ``x-control`` / etc.
   */
  xWidget?: string;
  /**
   * ``x-destructive`` annotation on enum fields — the enum value that should
   * require an inline "Are you sure?" confirmation before submit. When the
   * renderer treats the enum as pill-buttons, clicking this value shows a
   * confirmation strip instead of submitting immediately.
   */
  xDestructive?: string;
  /**
   * Sub-fields for array_of_objects items. Each sub-field is itself a full
   * FieldSchema, so renderers can dispatch on `type` recursively — an enum
   * sub-field gets a <select>, a multiline sub-field gets a <textarea>,
   * description / required flow through, etc.
   */
  itemFields?: FieldSchema[];
  /**
   * ``x-no-add`` annotation on an array_of_objects field — suppress the
   * "+ Add <singular>" row-adder button so the prefilled row(s) render
   * editable but the user cannot append new ones. The per-row Remove button
   * is unaffected. Used by the mortgagee prefill form (2026-06-22) so AMs edit
   * the prefilled mortgagee(s) without adding extras.
   */
  xNoAdd?: boolean;
  /**
   * ``x-item-label`` annotation on an array_of_objects field — replaces the
   * per-row card header ("<Singular> <n>") with this label. The row index is
   * appended only when the array holds more than one row, so a single-row
   * form reads as a plain heading (e.g. "New Mortgagee", split-address form
   * 2026-08-03).
   */
  xItemLabel?: string;
  required: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert a snake_case schema property key into a human-readable label —
 * `mortgagee_name` → `Mortgagee Name`. Used as the fallback when a property
 * has no explicit `title`. Matches the convention ora's legacy
 * WorkflowInputForm used so the parity lift is a visual no-op.
 *
 * Also used by renderers to humanize raw enum option strings (e.g.
 * `"approve"` → `"Approve"`, `"request_changes"` → `"Request Changes"`).
 */
export function titleCaseKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Pydantic v2 encodes `Optional[X]` as `{"anyOf": [{"type": "X"}, {"type": "null"}]}`.
 * Unwrap to the non-null member so nullable fields resolve to their actual type.
 * Returns null when the pattern doesn't match (e.g. true unions with >1 non-null branch).
 */
export function resolveNullableAnyOf(
  def: Record<string, unknown>,
): Record<string, unknown> | null {
  const variants = (def.anyOf ?? def.oneOf) as
    | Array<Record<string, unknown>>
    | undefined;
  if (!Array.isArray(variants)) return null;
  const nonNull = variants.filter((v) => v.type !== "null");
  if (nonNull.length === 1) return nonNull[0];
  return null;
}

/** Resolve a `{"$ref": "#/$defs/X"}` node against a schema root. Non-`$ref`
 *  nodes pass through unchanged; an unresolvable path yields `undefined`.
 *
 *  Exported (2026-08-27) for `launch/launchSchemaBinding.ts`, which slices the
 *  reshaped launch contract into a run-level and a per-action schema and must
 *  resolve `items.$ref` the same way this module already does. Re-implementing
 *  it there would fork `$ref` resolution across two files that parse the SAME
 *  schema — the fork `ui/CLAUDE.md` forbids. */
export function resolveSchemaRef(
  root: Record<string, unknown> | null | undefined,
  node: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!node) return undefined;
  const ref = node["$ref"];
  if (typeof ref !== "string") return node;
  const path = ref.replace(/^#\//, "").split("/");
  let cur: unknown = root;
  for (const seg of path) {
    cur = (cur as Record<string, unknown> | undefined)?.[seg];
  }
  return (cur as Record<string, unknown> | undefined) ?? undefined;
}

function hasPresentValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== "";
}

/**
 * Does ONE entry of a required array-of-objects carry every key its ITEM schema
 * declares required?
 *
 * Split out of `prefillSatisfiesRequired` so the gate can ask it of EVERY entry
 * rather than of `value[0]`: an `actions` array whose second action is missing
 * `policy_number` used to pass the gate untouched, and auto-launch fired on it.
 * `x-hidden` sub-keys are skipped for the same reason they are skipped at run
 * level — prefill never emits them.
 */
function itemSatisfiesRequired(
  itemDef: Record<string, unknown>,
  item: unknown,
): boolean {
  if (item === null || typeof item !== "object" || Array.isArray(item)) {
    return false;
  }
  const itemProps = (itemDef.properties ?? {}) as Record<string, unknown>;
  const itemRequired = Array.isArray(itemDef.required)
    ? (itemDef.required as string[])
    : [];
  for (const sub of itemRequired) {
    const subDef = itemProps[sub] as Record<string, unknown> | undefined;
    if (subDef?.["x-hidden"] === true) continue;
    if (!hasPresentValue((item as Record<string, unknown>)[sub])) return false;
  }
  return true;
}

/**
 * Does a prefill payload carry everything the schema says a launch REQUIRES?
 *
 * This is the auto-launch gate, lifted out of `InstanceDetailDrawer` so it can
 * be asserted without a browser. It answers the same question the flat
 * presence test used to, one level down:
 *
 *  - Run-level required keys keep today's presence test.
 *  - A required ARRAY-of-objects key (the v2 contract's `actions`) must be
 *    present, non-empty, AND EVERY entry must carry every key the ITEM schema
 *    declares required. Checking only that `actions` exists would pass on the
 *    KEY while proving nothing about the values — auto-launch would then start
 *    real runs on empty payloads, which is strictly worse than a gate that never
 *    fires. Checking only `value[0]` moved the same defect to index ≥ 1: two
 *    extracted actions, the second missing `policy_number`, and the gate opened.
 *  - `x-hidden` required keys are SKIPPED. Those are launcher-injected
 *    silent-capture inputs (`internet_message_id`, the term match keys) that
 *    prefill never emits and the AM never sees; leaving them in the presence
 *    test makes auto-launch permanently dead for every workflow that declares
 *    one. `idempotency_key` was already excluded by name for the same reason
 *    and stays excluded.
 *
 * A schema with no `required` list gates nothing — unchanged from today.
 */
export function prefillSatisfiesRequired(
  schema: Record<string, unknown> | null | undefined,
  prefill: Record<string, unknown> | null | undefined,
  options?: { ignore?: string[] },
): boolean {
  if (!schema || typeof schema !== "object") return true;
  const required = Array.isArray(schema.required)
    ? (schema.required as string[])
    : [];
  const props = (schema.properties ?? {}) as Record<string, unknown>;
  const ignore = new Set(options?.ignore ?? ["idempotency_key"]);

  for (const key of required) {
    if (ignore.has(key)) continue;
    const rawDef = props[key] as Record<string, unknown> | undefined;
    if (rawDef?.["x-hidden"] === true) continue;
    const def = rawDef ? (resolveNullableAnyOf(rawDef) ?? rawDef) : undefined;
    const value = prefill?.[key];

    const items = def?.items as Record<string, unknown> | undefined;
    if (def?.type === "array" && items) {
      if (!Array.isArray(value) || value.length === 0) return false;
      const itemDef = resolveSchemaRef(schema, items) ?? items;
      // EVERY entry, not `value[0]`. A second action missing a required field is
      // exactly the incomplete payload this gate exists to hold back, and it is
      // invisible to a first-entry-only check.
      if (!value.every((item) => itemSatisfiesRequired(itemDef, item))) {
        return false;
      }
      continue;
    }

    if (!hasPresentValue(value)) return false;
  }
  return true;
}

/**
 * Is every REQUIRED field in a parsed field list filled?
 *
 * The submit-readiness rule, and the ONE copy of it. Lived inside
 * `components/JsonSchemaForm.tsx` until 2026-08-27, when the reworked mortgagee
 * launch form needed the same rule for its per-action slice — which it renders
 * through a `JsonSchemaForm` with no footer, so it cannot read the renderer's
 * own gate. Two copies of a submit-readiness rule is how a form starts accepting
 * what the other rejects.
 *
 * Behaviour is unchanged from the version that lived in the renderer:
 *
 *  - booleans are exempt (false is a legitimate answer),
 *  - an `array_of_objects` field needs a non-empty array AND every entry must
 *    satisfy the ITEM schema's own required list. After the v2 reshape the
 *    run-level required list is just `actions`, and the fields the AM must fill
 *    (`policy_number`) are required one level down — asserting only "the array
 *    is non-empty" would let a policy-less launch through to the server's
 *    generic schema-validation error,
 *  - `undefined` / `null` / `""` fail.
 *
 * Lives HERE and not in `components/` so `schemaHelpers.ts` keeps its no-import
 * posture: this module must never import from `components/` (import cycle).
 */
export function checkAllRequiredFilled(
  fields: FieldSchema[],
  values: Record<string, unknown>,
): boolean {
  for (const f of fields) {
    if (!f.required) continue;
    if (f.type === "boolean") continue;
    const v = values[f.key];
    if (f.type === "array_of_objects") {
      if (!Array.isArray(v) || v.length === 0) return false;
      const itemFields = f.itemFields ?? [];
      if (itemFields.length === 0) continue;
      for (const item of v) {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          return false;
        }
        if (
          !checkAllRequiredFilled(itemFields, item as Record<string, unknown>)
        ) {
          return false;
        }
      }
      continue;
    }
    if (v === undefined || v === null || v === "") return false;
  }
  return true;
}

/**
 * Parse a JSON Schema `type: "object"` definition into a flat list of FieldSchema
 * descriptors. Returns null when the schema is absent or not an object schema.
 *
 * Supports:
 *   - Primitive types: string, number, integer, boolean
 *   - Enum fields (takes priority over type)
 *   - Pydantic v2 Optional[X] encoded as anyOf/oneOf with a null branch
 *   - Arrays of objects (type: "array" with items.properties or items.$ref)
 *   - Multiline strings (type: "multiline" — ora/substrate extension)
 *   - Unknown / unsupported types fall back to type: "unknown"
 */
export function parseObjectSchema(
  schema: Record<string, unknown> | null | undefined,
  // The root that the ``$ref``s in this subtree resolve against. Defaults to
  // ``schema`` itself, so every existing caller is byte-identical (one argument
  // == today's behaviour, exactly).
  //
  // A nested recursion MUST pass the ORIGINAL root: the recursion below rebuilds
  // a SYNTHETIC ``{type, properties, required}`` root that carries no ``$defs``,
  // so a ``$ref`` one level further down resolved to ``undefined`` and the field
  // fell out as ``type: "unknown"``. Nothing nested twice until the v2 launch
  // contract moved its per-action fields inside ``actions[]`` — whose items
  // carry ``mortgagees`` (``$ref`` → ``MortgageeRecord``) and ``target_interest``
  // (``$ref`` → ``TargetInterest``). Without this the AM silently loses the
  // mortgagee-record editor to an "unknown field" JSON textarea.
  refRoot: Record<string, unknown> | null | undefined = schema,
): FieldSchema[] | null {
  if (!schema || typeof schema !== "object") return null;
  if ((schema.type as string | undefined) !== "object") return null;
  const props = schema.properties as Record<string, unknown> | undefined;
  if (!props || typeof props !== "object") return null;

  const required = Array.isArray(schema.required)
    ? new Set(schema.required as string[])
    : new Set<string>();

  const fields: FieldSchema[] = [];
  for (const [key, raw] of Object.entries(props)) {
    if (!raw || typeof raw !== "object") continue;
    const def = raw as Record<string, unknown>;
    if (def["x-hidden"] === true) continue;
    const description =
      typeof def.description === "string" ? def.description : undefined;
    const label =
      typeof def.title === "string" ? (def.title as string) : titleCaseKey(key);

    // Pydantic v2 generates anyOf/oneOf for Optional[X] fields.
    const effectiveDef = resolveNullableAnyOf(def) ?? def;

    // Enum takes priority over raw type.
    const enumVals = Array.isArray(effectiveDef.enum)
      ? (effectiveDef.enum as unknown[])
      : null;
    if (enumVals && enumVals.length > 0) {
      const xDestructiveRaw = def["x-destructive"] ?? effectiveDef["x-destructive"];
      const xDestructive =
        typeof xDestructiveRaw === "string" && xDestructiveRaw.length > 0
          ? xDestructiveRaw
          : undefined;
      // x-enum-titles is a parallel array of display labels. The substrate
      // uses it to keep stable enum keys (e.g. "text_sms") while surfacing
      // AM-friendly labels (e.g. "Text a code to my phone") to the UI.
      // Accept the annotation on either def (the property) or effectiveDef
      // (post Optional[X] unwrap).
      const enumTitlesRaw = def["x-enum-titles"] ?? effectiveDef["x-enum-titles"];
      const enumLabels =
        Array.isArray(enumTitlesRaw) &&
        enumTitlesRaw.every((t) => typeof t === "string")
          ? (enumTitlesRaw as string[])
          : undefined;
      const xRenderRaw = def["x-render"] ?? effectiveDef["x-render"];
      const xRender =
        typeof xRenderRaw === "string" && xRenderRaw.length > 0
          ? xRenderRaw
          : undefined;
      const xRenderDirectionRaw =
        def["x-render-direction"] ?? effectiveDef["x-render-direction"];
      const xRenderDirection =
        typeof xRenderDirectionRaw === "string" && xRenderDirectionRaw.length > 0
          ? xRenderDirectionRaw
          : undefined;
      fields.push({
        key,
        label,
        description,
        type: "enum",
        enumValues: enumVals.map((v) => String(v)),
        enumLabels,
        xDestructive,
        xRender,
        xRenderDirection,
        required: required.has(key),
      });
      continue;
    }

    // ``x-widget`` is read on every property regardless of underlying type,
    // since it overrides the default widget choice (e.g. a list-of-objects
    // can render as a file-upload dropzone instead of the JSON textarea).
    const xWidgetRaw = def["x-widget"] ?? effectiveDef["x-widget"];
    const xWidget =
      typeof xWidgetRaw === "string" && xWidgetRaw.length > 0
        ? xWidgetRaw
        : undefined;

    const type = effectiveDef.type;
    if (type === "string") {
      const format = typeof effectiveDef.format === "string" ? effectiveDef.format : undefined;
      const xSensitive = typeof def["x-sensitive"] === "string" ? (def["x-sensitive"] as string) : undefined;
      fields.push({ key, label, description, type: "string", format, xSensitive, xWidget, required: required.has(key) });
    } else if (type === "boolean") {
      fields.push({ key, label, description, type: "boolean", xWidget, required: required.has(key) });
    } else if (type === "number" || type === "integer") {
      fields.push({
        key,
        label,
        description,
        type: type as "number" | "integer",
        xWidget,
        required: required.has(key),
      });
    } else if (type === "array" || type === "multiline") {
      // "multiline" is a Pydantic ui-hint (Field(type="multiline")) that overrides
      // the JSON Schema "type": "array". When "items" is present the field is still
      // an array — resolve it as array_of_objects so the correct textarea renderer
      // fires. When "items" is absent (plain multiline string), fall back to "multiline".
      const items = effectiveDef.items as Record<string, unknown> | undefined;
      let itemProps = items?.properties as Record<string, unknown> | undefined;
      let itemRequired = items?.required as string[] | undefined;
      if (!itemProps && typeof items?.["$ref"] === "string") {
        const refPath = (items["$ref"] as string).replace(/^#\//, "").split("/");
        let node: unknown = refRoot ?? schema;
        for (const seg of refPath) {
          node = (node as Record<string, unknown>)?.[seg];
        }
        const refTarget = node as Record<string, unknown> | undefined;
        itemProps = refTarget?.properties as Record<string, unknown> | undefined;
        itemRequired = refTarget?.required as string[] | undefined;
      }
      if (itemProps && typeof itemProps === "object") {
        // Recurse: each sub-field gets the same FieldSchema treatment as
        // a top-level field, so multiline / enum / number / required all
        // flow through automatically.
        const itemFields =
          parseObjectSchema(
            {
              type: "object",
              properties: itemProps,
              required: itemRequired,
            },
            // The ORIGINAL root, never the synthetic object above — that is the
            // only thing carrying ``$defs`` for a ``$ref`` one level deeper.
            refRoot ?? schema,
          ) ?? [];
        fields.push({
          key,
          label,
          description,
          type: "array_of_objects",
          xWidget,
          itemFields,
          xNoAdd: def["x-no-add"] === true,
          ...(typeof def["x-item-label"] === "string" && def["x-item-label"]
            ? { xItemLabel: def["x-item-label"] as string }
            : {}),
          required: required.has(key),
        });
      } else if (type === "multiline") {
        fields.push({ key, label, description, type: "multiline", xWidget, required: required.has(key) });
      } else {
        fields.push({ key, label, description, type: "unknown", xWidget, required: required.has(key) });
      }
    } else {
      fields.push({ key, label, description, type: "unknown", xWidget, required: required.has(key) });
    }
  }
  return fields;
}
